/**
 * Date utility functions for handling epoch seconds vs milliseconds
 */

/**
 * Handles dates that might be provided as epoch seconds or milliseconds
 * JavaScript's Date constructor expects milliseconds, but our API sends seconds
 * @param {string|number|Date|Array} date - date input (string, timestamp, Date object, or array)
 * @returns {Date} JavaScript Date object adjusted for proper time handling
 */
export function parseDate(date) {
  if (!date) return new Date();
  
  // If already a Date object, just return it
  if (date instanceof Date) return date;
  
  // Handle array format from backend: [year, month, day, hour, minute, second, nanosecond]
  if (Array.isArray(date) && date.length >= 3) {
    // Note: JavaScript months are 0-based (0 = January, 11 = December)
    // Backend sends 1-based months, so we subtract 1
    const year = date[0];
    const month = date[1] - 1; // Adjust month for JS Date (0-based)
    const day = date[2];
    const hour = date.length > 3 ? date[3] : 0;
    const minute = date.length > 4 ? date[4] : 0;
    const second = date.length > 5 ? date[5] : 0;
    const ms = date.length > 6 ? Math.floor(date[6] / 1000000) : 0; // Convert nanoseconds to milliseconds
    
    return new Date(year, month, day, hour, minute, second, ms);
  }
  
  // If it's a number and appears to be epoch seconds (not milliseconds)
  // Number less than 10 billion is almost certainly seconds, not milliseconds
  // (valid until year 2286)
  if (typeof date === 'number' && date < 10000000000) {
    return new Date(date * 1000); // Convert seconds to milliseconds
  }
  
  // Otherwise, use standard Date constructor (works for ISO strings or millisecond timestamps)
  return new Date(date);
}

/**
 * Formats a date as a locale date string (e.g., "6/20/2025")
 * @param {string|number|Date} date - date input (string, timestamp, or Date object)
 * @returns {string} Formatted date string
 */
export function formatDateString(date) {
  if (!date) return 'N/A';
  return parseDate(date).toLocaleDateString();
}

/**
 * Formats a date as a locale time string (e.g., "10:30:45 AM")
 * @param {string|number|Date} date - date input (string, timestamp, or Date object)
 * @returns {string} Formatted time string
 */
export function formatTimeString(date) {
  if (!date) return 'N/A';
  return parseDate(date).toLocaleTimeString();
}

/**
 * Formats a date as a full locale date and time string (e.g., "6/20/2025, 10:30:45 AM")
 * @param {string|number|Date} date - date input (string, timestamp, or Date object)
 * @returns {string} Formatted date and time string
 */
export function formatFullDateTimeString(date) {
  if (!date) return 'N/A';
  return parseDate(date).toLocaleString();
}
